import { Component, HostListener, OnInit } from "@angular/core";
import { UntilDestroy } from "@ngneat/until-destroy";
import { catchError, from, mergeMap, of, Subscription, switchMap, takeUntil, takeWhile, timer } from "rxjs";
import { MqttEventService } from "../shared/services/mqtt-event.service";
import { RealtimeEventService } from "../shared/services/realtime-event.service";
import { LoanDashboardService } from "./components/loan-dashboard/loan-dashboard.service";
import { DashboardEvent } from "../shared/constant/content";
import { TimelineService } from "./components/loan-dashboard/components/timeline/timeline.service";
import { StoreService } from "../shared/services/store.service";
import { FCMService } from "../shared/services/fcm.service";
import { CommonService } from "../shared/services/common.service";

@UntilDestroy({ arrayName: "subscriptions" })
@Component({
    selector: "web-loan-layout",
    templateUrl: "./loan-layout.component.html",
    styleUrls: ["./loan-layout.component.scss"],
})
export class LoanLayoutComponent implements OnInit {
    subscriptions: Subscription[] = [];
    fcmToken: string | null = null;

    constructor(
        private mqtt: MqttEventService,
        private realtimeEventService: RealtimeEventService,
        public loanDashboardService: LoanDashboardService,
        private timelineService: TimelineService,
        private storeService: StoreService,
        private commonService: CommonService,
        private fcmService: FCMService
    ) {
        this.mqtt.createConnection();
        this.realtimeEventService.initializeListeners();
        this.getRealtimeEvent();
        this.setFcmToken();
    }

    getRealtimeEvent() {
        this.subscriptions.push(
            this.realtimeEventService.realtimeEvent$.subscribe((event: any) => {
                if (DashboardEvent.includes(event?.status)) {
                    setTimeout(() => {
                        this.loanDashboardService
                            .getDashboardDetails$(false, false)
                            .pipe(
                                switchMap((res) => {
                                    if (res) {
                                        if (res?.stage === "TIMELINE") {
                                            return from(this.timelineService.goToNextStep(res));
                                        } else {
                                            return from(this.loanDashboardService.handleStage(res));
                                        }
                                    }
                                    return from(Promise.resolve());
                                })
                            )
                            .subscribe();
                    }, 500);
                }

                if (event?.status === "GET_REAL_TIME_CUSTOMER_LOCATION") {
                    this.loanDashboardService.getCustomerLocation().subscribe({
                        next: (res) => {
                            // Optionally handle response
                        },
                        error: (err) => {
                            // Optionally handle error
                        },
                    });
                }
            })
        );
    }

    async setFcmToken() {
        let isGeneratedAuthToken = this.storeService.get("isGeneratedAuthToken");
        if (isGeneratedAuthToken == "true") {
            return;
        }

        const stopTrying$ = timer(120000);
        timer(0, 10000) // Emit the first value immediately and then every second
            .pipe(
                takeUntil(stopTrying$),
                takeWhile(() => this.fcmToken == null || this.fcmToken == ""), // Continue until fcmToken is set
                mergeMap(() =>
                    this.fcmService.requestPermission().pipe(
                        mergeMap((permission) => {
                            if (!permission) {
                                return of("");
                            }
                            return this.fcmService.requestToken();
                        })
                    )
                ),
                catchError(() => of(null)) // Handle errors by emitting null
            )
            .subscribe({
                next: (token) => {
                    this.fcmToken = token;
                    if (this.fcmToken != null && this.fcmToken != "") {
                        this.storeService.set({
                            key: "fcmToken",
                            value: JSON.stringify(this.fcmToken),
                        });
                        this.commonService.updateToken({ notificationToken: this.fcmToken.toString() }).subscribe();
                    }
                },
                error: (error) => {
                    console.error("Error:", error);
                },
                complete: () => {
                    console.log("Token:", this.fcmToken);
                },
            });
    }

    ngOnInit(): void {
        this.parkHistoryGuard();
    }

    @HostListener("window:popstate", ["$event"])
    onPopState(event: PopStateEvent) {
        // Re-park before refreshing, so the next press is caught as well.
        this.parkHistoryGuard();
        this.loanDashboardService.getDashboardDetails(true);
    }

    /**
     * Parks a duplicate history entry on the stack. popstate cannot be cancelled,
     * so without it the back press would move the browser to the previous URL and
     * race with the stage redirect below — and on a deep link into a step, drop
     * the user out of the app entirely. Popping this entry leaves the URL
     * untouched, which makes onPopState the only thing that moves the user.
     */
    private parkHistoryGuard(): void {
        // Keep the router's own state (navigationId) on the parked entry.
        history.pushState({ ...(history.state || {}) }, "", window.location.href);
    }
}
